import React, { MouseEvent, FC, memo, useCallback, useRef } from "react";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Grow from "@mui/material/Grow";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import MenuList from "@mui/material/MenuList";
import MenuItem from "@mui/material/MenuItem";
import Popper from "@mui/material/Popper";
import Paper from "@mui/material/Paper";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

export interface AppMenu {
  name: string;
  onClick?: (e: MouseEvent<any>) => void;
  subMenus?: AppMenu[];
}

export interface GenAppMenuProps {
  menu: AppMenu;
  open: boolean;
  onToggle?: (open: boolean) => void;
  onClickAway?: () => void;
}

const GenAppMenu: FC<GenAppMenuProps> = (props) => {
  const { menu, open, onClickAway, onToggle } = props;
  const { onClick, name, subMenus } = menu;
  const [selectedIndex, setSelectedIndex] = React.useState(1);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleMenuItemClick = useCallback(
    (event: MouseEvent<HTMLLIElement>, index: number) => {
      setSelectedIndex(index);
      const subMenu = subMenus ? subMenus[index] : undefined;
      if (subMenu?.onClick) {
        subMenu.onClick(event);
      }
    },
    [subMenus]
  );

  const handleOnClick = useCallback(
    (e: MouseEvent) => {
      if (subMenus && onToggle) {
        onToggle(open);
      } else if (onClick) {
        onClick(e);
      }
    },
    [onClick, onToggle, open, subMenus]
  );

  return (
    <>
      <Button
        variant="text"
        ref={buttonRef}
        aria-controls={open ? "split-button-menu" : undefined}
        aria-expanded={open ? "true" : undefined}
        aria-haspopup="menu"
        onClick={handleOnClick}
        sx={{
          my: 1,
          mx: 2,
          mb: 0.5,
          color: "white",
          display: "block",
          textTransform: "none",
        }}
      >
        <Typography noWrap color="white" display={"inline-flex"} gap={1}>
          {name}
          {subMenus ? <ArrowDropDownIcon /> : null}
        </Typography>{" "}
      </Button>
      {subMenus && onClickAway && (
        <Popper
          sx={{
            zIndex: 1,
          }}
          open={open}
          role={undefined}
          transition
          disablePortal
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === "bottom" ? "center top" : "center bottom",
              }}
            >
              <Paper
                sx={{
                  position: "absolute",
                  top: buttonRef.current
                    ? buttonRef.current?.offsetTop +
                      buttonRef.current?.offsetHeight
                    : 0,
                  left: buttonRef.current ? buttonRef.current?.offsetLeft : 0,
                }}
              >
                <ClickAwayListener onClickAway={onClickAway}>
                  <MenuList autoFocusItem>
                    {subMenus.map((option, index) => (
                      <MenuItem
                        key={option.name}
                        selected={index === selectedIndex}
                        onClick={(event) => handleMenuItemClick(event, index)}
                      >
                        {option.name}
                      </MenuItem>
                    ))}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      )}
    </>
  );
};

export default memo(GenAppMenu);
